import { addDays, addMonths, clampDay, isCivilDateAfter, isCivilDateBefore, } from './civil-date.js';
const MONTHS_PER_YEAR = 12;
const MIN_ANCHOR_DAY = 1;
const MAX_ANCHOR_DAY = 31;
export const monthsPerPeriod = (intervalUnit, intervalCount) => {
    if (!Number.isInteger(intervalCount) || intervalCount < 1) {
        throw new RangeError(`intervalCount must be a positive integer, received ${intervalCount}`);
    }
    return intervalUnit === 'year' ? intervalCount * MONTHS_PER_YEAR : intervalCount;
};
const anchorMonthOf = (rule) => {
    if (rule.intervalUnit !== 'year') {
        return rule.startDate.month;
    }
    const anchorMonth = rule.anchorMonth ?? null;
    if (anchorMonth === null) {
        throw new RangeError('anchorMonth is required for a yearly recurrence');
    }
    if (!Number.isInteger(anchorMonth) || anchorMonth < 1 || anchorMonth > MONTHS_PER_YEAR) {
        throw new RangeError(`anchorMonth must be between 1 and 12, received ${anchorMonth}`);
    }
    return anchorMonth;
};
const assertValidRule = (rule) => {
    monthsPerPeriod(rule.intervalUnit, rule.intervalCount);
    anchorMonthOf(rule);
    const isAnchorDayInRange = Number.isInteger(rule.anchorDay) &&
        rule.anchorDay >= MIN_ANCHOR_DAY &&
        rule.anchorDay <= MAX_ANCHOR_DAY;
    if (!isAnchorDayInRange) {
        throw new RangeError(`anchorDay must be between 1 and 31, received ${rule.anchorDay}`);
    }
    const endDate = rule.endDate ?? null;
    if (endDate !== null && isCivilDateBefore(endDate, rule.startDate)) {
        throw new RangeError('endDate must not be earlier than startDate');
    }
};
/**
 * The unclamped anchor the whole series is measured from: the first anchor date that is not
 * earlier than `startDate`. Its `day` stays at `anchorDay` even when that day does not exist
 * in the month — every period is derived from this origin, never from a previously clamped
 * date, which is what keeps a quarterly series anchored on the 30th from sliding to the 28th
 * after its first February.
 */
export const recurrenceOrigin = (rule) => {
    assertValidRule(rule);
    const candidate = {
        year: rule.startDate.year,
        month: anchorMonthOf(rule),
        day: rule.anchorDay,
    };
    const isCandidateEarlyForStart = isCivilDateBefore(clampDay(candidate), rule.startDate);
    const monthsToSkip = rule.intervalUnit === 'year' ? MONTHS_PER_YEAR : 1;
    return isCandidateEarlyForStart ? addMonths(candidate, monthsToSkip) : candidate;
};
const buildPeriod = (rule, origin, step, index) => {
    const dueDate = clampDay(addMonths(origin, index * step));
    const endDate = rule.endDate ?? null;
    if (endDate !== null && isCivilDateAfter(dueDate, endDate)) {
        return null;
    }
    const nextDueDate = clampDay(addMonths(origin, (index + 1) * step));
    const seriesLimit = endDate === null ? null : addDays(endDate, 1);
    if (seriesLimit !== null && isCivilDateBefore(seriesLimit, nextDueDate)) {
        return { index, dueDate, periodStart: dueDate, periodEnd: seriesLimit };
    }
    return { index, dueDate, periodStart: dueDate, periodEnd: nextDueDate };
};
export const periodAt = (rule, index) => {
    if (!Number.isInteger(index) || index < 0) {
        throw new RangeError(`index must be a non-negative integer, received ${index}`);
    }
    const origin = recurrenceOrigin(rule);
    return buildPeriod(rule, origin, monthsPerPeriod(rule.intervalUnit, rule.intervalCount), index);
};
const absoluteMonthOf = (date) => date.year * MONTHS_PER_YEAR + (date.month - 1);
const firstIndexNotBefore = (origin, step, from) => {
    const monthsFromOrigin = absoluteMonthOf(from) - absoluteMonthOf(origin);
    return Math.max(0, Math.ceil(monthsFromOrigin / step));
};
export const nextPeriodOn = (rule, from) => {
    const origin = recurrenceOrigin(rule);
    const step = monthsPerPeriod(rule.intervalUnit, rule.intervalCount);
    const startIndex = firstIndexNotBefore(origin, step, from);
    const candidate = buildPeriod(rule, origin, step, startIndex);
    if (candidate === null) {
        return null;
    }
    if (!isCivilDateBefore(candidate.dueDate, from)) {
        return candidate;
    }
    return buildPeriod(rule, origin, step, startIndex + 1);
};
export const listPeriods = (rule, range) => {
    if (isCivilDateAfter(range.from, range.to)) {
        throw new RangeError('range.from must not be later than range.to');
    }
    const origin = recurrenceOrigin(rule);
    const step = monthsPerPeriod(rule.intervalUnit, rule.intervalCount);
    const periods = [];
    let index = firstIndexNotBefore(origin, step, range.from);
    let period = buildPeriod(rule, origin, step, index);
    while (period !== null && !isCivilDateAfter(period.dueDate, range.to)) {
        if (!isCivilDateBefore(period.dueDate, range.from)) {
            periods.push(period);
        }
        index += 1;
        period = buildPeriod(rule, origin, step, index);
    }
    return periods;
};
//# sourceMappingURL=recurrence.js.map